import type { MouseEventHandler, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { AnimateHeight } from '../../AnimateHeight';
import { ButtonBase } from '../../ButtonBase';
import { Text } from '../../Text';
import { Typography } from '../../Typography';
import type { AccordionTheme } from '../themes';
import { AccordionVariant, themes } from '../themes';

export interface AccordionPanelProps {
  /** The id of the panel, used for accessibility. */
  id: string;
  /** Whether the panel is expanded or not. */
  expanded?: boolean;
  /** The heading of the panel that when control the content visibility. */
  heading: ReactNode;
  /** The content of the panel. */
  children: ReactNode;
  /** The theme overridden by the {@link Accordion} variant prop. */
  theme?: AccordionTheme;
  /** The variant overridden by the {@link Accordion} variant prop. */
  variant?: AccordionVariant;
  /** Callback when the accordion panel height is toggled. */
  onToggle?: (expanded: boolean) => void;
}

const defaultTheme = themes['Default'];

/**
 * Render a panel in an {@link Accordion} container.
 *
 * @example
 *
 * ```tsx
 * <Accordion>
 *   <AccordionPanel heading="Heading 1">Content 1</AccordionPanel>
 *   <AccordionPanel heading="Heading 2">Content 2</AccordionPanel>
 * </Accordion>;
 * ```
 */

export function AccordionPanel(props: AccordionPanelProps) {
  const {
    theme = defaultTheme,
    variant,
    expanded: initiallyExpanded = false,
  } = props;
  const [collapsedIcon, expandedIcon] = theme.headingIcons;
  const isFoundationVariant = variant === AccordionVariant.Foundation;
  const controlId = `${props.id}-control`;
  const regionId = `${props.id}-details`;

  const [expanded, setExpanded] = useState(initiallyExpanded);

  const handleHeadingClick: MouseEventHandler<HTMLButtonElement> = () => {
    setExpanded(!expanded);
    props.onToggle?.(!expanded);
  };

  const regionRef = useRef<HTMLDivElement>(null);

  // Sync expanded state with prop
  useEffect(() => setExpanded(initiallyExpanded), [initiallyExpanded]);

  return (
    <div id={props.id} className={theme.className}>
      <ButtonBase
        type="button"
        id={controlId}
        onClick={handleHeadingClick}
        aria-controls={regionId}
        aria-expanded={expanded}
      >
        {isFoundationVariant ? (
          <Typography
            as="h3"
            variant="display"
            size={{ xs: 21, lg: 24 }}
            color="Kale3"
          >
            {props.heading}
          </Typography>
        ) : (
          <Text as="h3" {...theme.headingProps}>
            {props.heading}
          </Text>
        )}
        <span aria-hidden>{expanded ? expandedIcon : collapsedIcon}</span>
      </ButtonBase>
      <AnimateHeight nodeRef={regionRef} animateIn={expanded}>
        <div
          ref={regionRef}
          id={regionId}
          role="region"
          aria-labelledby={controlId}
          hidden={!expanded}
        >
          {props.children}
        </div>
      </AnimateHeight>
    </div>
  );
}
